import { Maximize2, MessageCircle } from 'lucide-react'
import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { Button } from '@/components/ui/Button'
import { Sheet } from '@/components/ui/Sheet'
import { useT } from '@/i18n'
import type { ChatKind } from '@/services/core/chat.service'
import { useStores } from '@/stores'
import { ChatList } from './ChatList'
import { Conversation } from './Conversation'

const POLL_MS = 15_000

/**
 * The top bar's chat icon: the total unread badge (polled every 15 s and on
 * focus) and the right-hand drawer. Hidden without chat.use.
 */
export const ChatButton = observer(function ChatButton() {
  const t = useT()
  const { session, chat } = useStores()
  const enabled = session.can('chat.use')

  useEffect(() => {
    if (!enabled) return
    void chat.refreshUnread()
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void chat.refreshUnread()
    }, POLL_MS)
    const onFocus = () => void chat.refreshUnread()
    window.addEventListener('focus', onFocus)
    return () => {
      window.clearInterval(timer)
      window.removeEventListener('focus', onFocus)
    }
  }, [enabled, chat])

  if (!enabled) return null
  const unread = chat.unread.total
  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => (chat.drawerOpen ? chat.closeDrawer() : chat.openDrawer(chat.drawerChatId))}
        aria-label={unread > 0 ? `${t('chat.open')} · ${t('chat.unread', { count: unread })}` : t('chat.open')}
        title={t('chat.open')}
        className="relative"
      >
        <MessageCircle className="size-4" />
        {unread > 0 && (
          <span className="num absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-danger px-1 text-[10px] font-semibold leading-none text-white">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </Button>
      <ChatDrawer />
    </>
  )
})

/** «Заказы / Личные / Группы», search and list; a chat opens inside the drawer with a back arrow. */
const ChatDrawer = observer(function ChatDrawer() {
  const t = useT()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { chat } = useStores()
  const [kind, setKind] = useState<ChatKind>('order')
  const close = useCallback(() => chat.closeDrawer(), [chat])

  // Going to another page (e.g. «Открыть заказ») closes the drawer.
  const [path, setPath] = useState(pathname)
  if (path !== pathname) {
    setPath(pathname)
    if (chat.drawerOpen) chat.closeDrawer()
  }

  return (
    <Sheet
      open={chat.drawerOpen}
      onClose={close}
      bare
      width="md"
      title={t('chat.title')}
      headerAside={
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={t('chat.openPage')}
          title={t('chat.openPage')}
          onClick={() => {
            const id = chat.drawerChatId
            chat.closeDrawer()
            navigate(id ? `/chats?chat=${encodeURIComponent(id)}` : '/chats')
          }}
        >
          <Maximize2 className="size-3.5" />
        </Button>
      }
    >
      {chat.drawerChatId ? (
        <Conversation key={chat.drawerChatId} chatId={chat.drawerChatId} onBack={() => chat.showInDrawer(null)} />
      ) : (
        <ChatList kind={kind} onKindChange={setKind} onOpen={(row) => chat.showInDrawer(row.id)} />
      )}
    </Sheet>
  )
})
